/**
 * Agent Invitation Acceptance Page
 * 
 * Allows invited agents to:
 * 1. View invitation details
 * 2. Create their account with chosen password
 * 3. Automatically login after account creation
 */

import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Alert } from '../../components/ui/Alert'
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  Users, 
  Mail,
  Lock,
  User,
  RefreshCw,
  AlertTriangle
} from 'lucide-react'

import { api } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import { logger } from '../../utils/logger'

interface InvitationStatus {
  status: 'valid' | 'expired' | 'already_accepted' | 'invalid'
  email?: string
  role?: string
  team_name?: string
  agent_name?: string
  expires_at?: string
  message: string
}

interface AcceptInvitationData {
  invitation_token: string
  password: string
  full_name: string
}

export const AgentInvitationAcceptance: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { login } = useAuth()
  
  const token = searchParams.get('token')
  
  const [invitationStatus, setInvitationStatus] = useState<InvitationStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAccepting, setIsAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Form data
  const [formData, setFormData] = useState({
    full_name: '',
    password: '',
    confirm_password: ''
  })
  
  // Check invitation status on component mount
  useEffect(() => {
    if (!token) {
      setError('No invitation token provided')
      setIsLoading(false)
      return
    }
    
    checkInvitationStatus()
  }, [token])
  
  const checkInvitationStatus = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      const response = await api.get(`/agents/auth/invitation/${token}`)
      setInvitationStatus(response.data)
      
      // Pre-fill agent name if available
      if (response.data.agent_name) {
        setFormData(prev => ({
          ...prev,
          full_name: response.data.agent_name
        }))
      }
      
      logger.info('Invitation status checked', {
        component: 'AgentInvitationAcceptance',
        status: response.data.status
      })
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to check invitation'
      setError(errorMessage)
      
      logger.error('Failed to check invitation status', {
        component: 'AgentInvitationAcceptance',
        error: errorMessage
      })
    } finally {
      setIsLoading(false)
    }
  }
  
  const handleAcceptInvitation = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!token || !invitationStatus) {
      setError('Invalid invitation')
      return
    }
    
    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match')
      return
    }
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }
    
    try {
      setIsAccepting(true)
      setError(null)
      
      const acceptData: AcceptInvitationData = {
        invitation_token: token,
        password: formData.password,
        full_name: formData.full_name
      }
      
      const response = await api.post('/agents/auth/accept-invitation', acceptData)
      
      // Store authentication token in the same format as company owner login
      const authData = {
        state: {
          user: {
            id: response.data.user_id || 'temp_id',
            email: invitationStatus.email || '',
            full_name: formData.full_name,
            is_active: true,
            user_type: response.data.user_type || 'agent'
          },
          token: response.data.access_token,
          isAuthenticated: true
        }
      }
      
      localStorage.setItem('bgai-auth-storage', JSON.stringify(authData))
      localStorage.setItem('user_type', response.data.user_type)
      
      // Set token in axios headers immediately
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`
      
      logger.info('Agent invitation accepted successfully', {
        component: 'AgentInvitationAcceptance',
        email: invitationStatus.email
      })
      
      // Redirect to agent dashboard
      navigate('/agent/dashboard')
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to accept invitation'
      setError(errorMessage)
      
      logger.error('Failed to accept invitation', {
        component: 'AgentInvitationAcceptance',
        error: errorMessage
      })
    } finally {
      setIsAccepting(false)
    }
  }
  
  const renderInvitationStatus = () => {
    if (!invitationStatus) return null
    
    const statusConfig = {
      valid: {
        icon: CheckCircle,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      },
      expired: {
        icon: Clock,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      },
      already_accepted: {
        icon: XCircle,
        color: 'text-gray-600',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200'
      },
      invalid: {
        icon: AlertTriangle,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      }
    }
    
    const config = statusConfig[invitationStatus.status]
    const StatusIcon = config.icon
    
    return (
      <Alert className={`${config.bgColor} ${config.borderColor} mb-6`}>
        <StatusIcon className={`w-5 h-5 ${config.color}`} />
        <div>
          <h4 className={`font-medium ${config.color} mb-1`}>
            {invitationStatus.status === 'valid' ? 'Invitation Valid' :
             invitationStatus.status === 'expired' ? 'Invitation Expired' :
             invitationStatus.status === 'already_accepted' ? 'Already Accepted' :
             'Invalid Invitation'}
          </h4>
          <p className="text-sm text-gray-600">{invitationStatus.message}</p>
        </div>
      </Alert>
    )
  }
  
  const renderInvitationDetails = () => {
    if (!invitationStatus || invitationStatus.status !== 'valid') return null
    
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-blue-900 mb-3 flex items-center">
          <Users className="w-5 h-5 mr-2" />
          Invitation Details
        </h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-blue-700">Email:</span>
            <span className="font-medium text-blue-900">{invitationStatus.email}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-blue-700">Team:</span>
            <span className="font-medium text-blue-900">{invitationStatus.team_name}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-blue-700">Role:</span>
            <Badge variant="outline" className="text-blue-700">
              {invitationStatus.role}
            </Badge>
          </div>
          {invitationStatus.expires_at && (
            <div className="flex items-center justify-between">
              <span className="text-blue-700">Expires:</span>
              <span className="font-medium text-blue-900">
                {new Date(invitationStatus.expires_at).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>
      </div>
    )
  }
  
  const renderAcceptanceForm = () => {
    if (!invitationStatus || invitationStatus.status !== 'valid') return null
    
    return (
      <form onSubmit={handleAcceptInvitation} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4 inline mr-1" />
            Full Name *
          </label>
          <Input
            type="text"
            value={formData.full_name}
            onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
            placeholder="Enter your full name"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Lock className="w-4 h-4 inline mr-1" />
            Password *
          </label>
          <Input
            type="password"
            value={formData.password}
            onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
            placeholder="Choose a secure password (minimum 8 characters)"
            required
            minLength={8}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Lock className="w-4 h-4 inline mr-1" />
            Confirm Password *
          </label>
          <Input
            type="password"
            value={formData.confirm_password}
            onChange={(e) => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
            placeholder="Confirm your password"
            required
            minLength={8}
          />
        </div>
        
        <Button
          type="submit"
          disabled={isAccepting || !formData.full_name || !formData.password || !formData.confirm_password}
          className="w-full"
        >
          {isAccepting ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Creating Account...
            </>
          ) : (
            'Accept Invitation & Create Account'
          )}
        </Button>
      </form>
    )
  }
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-center">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mr-2" />
              <span className="text-gray-600">Checking invitation...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center">
            <Mail className="w-6 h-6 mr-2 text-blue-600" />
            Agent Invitation
          </CardTitle>
          <CardDescription>
            Welcome to CleverAct AI! Complete your account setup to join your team.
          </CardDescription>
        </CardHeader>
        
        <CardContent className="p-6 pt-0">
          {error && (
            <Alert className="bg-red-50 border-red-200 mb-6">
              <XCircle className="w-5 h-5 text-red-600" />
              <div>
                <h4 className="font-medium text-red-600 mb-1">Error</h4>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </Alert>
          )}
          
          {renderInvitationStatus()}
          {renderInvitationDetails()}
          {renderAcceptanceForm()}
          
          {invitationStatus?.status !== 'valid' && (
            <div className="text-center mt-6">
              <Button
                variant="outline"
                onClick={() => navigate('/auth/login')}
              >
                Go to Login
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default AgentInvitationAcceptance